/**
 * サブスクリプション処理中フラグ管理ユーティリティ
 *
 * 目的:
 * - Stripe契約処理中のUI保護
 * - ページリロード時の重複契約防止
 * - localStorage による永続化
 *
 * 使用例:
 * ```typescript
 * // 契約開始時
 * setSubscriptionProcessing(companyId, 'standard', 'card');
 *
 * // 状態確認
 * if (isSubscriptionProcessing(companyId)) {
 *   // 処理中UI表示
 * }
 *
 * // 完了時
 * clearSubscriptionProcessing(companyId);
 * ```
 */

import { logger } from './logger';

const PROCESSING_FLAG_PREFIX = 'avs_subscription_processing_';
const PROCESSING_TIMEOUT_MS = 10 * 60 * 1000; // 10分

export interface SubscriptionProcessingState {
  companyId: string;
  isProcessing: boolean;
  startedAt: string;  // ISO 8601形式
  planCode: string;
  method: 'card' | 'checkout';
}

/**
 * 契約処理中フラグを設定
 *
 * @param companyId - 会社ID
 * @param planCode - プランコード
 * @param method - 決済方法
 */
export function setSubscriptionProcessing(
  companyId: string,
  planCode: string,
  method: 'card' | 'checkout'
): void {
  if (!companyId) {
    logger.warn('[subscriptionProcessingLock] companyId is required');
    return;
  }

  const state: SubscriptionProcessingState = {
    companyId,
    isProcessing: true,
    startedAt: new Date().toISOString(),
    planCode,
    method,
  };

  const key = `${PROCESSING_FLAG_PREFIX}${companyId}`;

  try {
    localStorage.setItem(key, JSON.stringify(state));
    logger.info('[subscriptionProcessingLock] Processing flag set', { companyId, planCode, method });
  } catch (error) {
    logger.error('[subscriptionProcessingLock] Failed to set processing flag', error);
    // localStorageが使えない場合は続行（graceful degradation）
  }
}

/**
 * 契約処理中フラグを取得（タイムアウトチェック付き）
 *
 * @param companyId - 会社ID
 * @returns 処理中の場合は状態、そうでない場合はnull
 */
export function getSubscriptionProcessing(
  companyId: string
): SubscriptionProcessingState | null {
  if (!companyId) {
    return null;
  }

  const key = `${PROCESSING_FLAG_PREFIX}${companyId}`;

  try {
    const raw = localStorage.getItem(key);

    if (!raw) {
      return null;
    }

    const state: SubscriptionProcessingState = JSON.parse(raw);

    // タイムアウトチェック
    const elapsed = Date.now() - new Date(state.startedAt).getTime();
    if (elapsed >= PROCESSING_TIMEOUT_MS) {
      // タイムアウト → フラグをクリア
      logger.warn('[subscriptionProcessingLock] Processing timeout detected', {
        companyId,
        elapsed,
        timeoutMs: PROCESSING_TIMEOUT_MS,
      });
      clearSubscriptionProcessing(companyId);
      return null;
    }

    return state;
  } catch (error) {
    logger.error('[subscriptionProcessingLock] Failed to get processing flag', error);
    // パース失敗 → フラグをクリア
    clearSubscriptionProcessing(companyId);
    return null;
  }
}

/**
 * 契約処理中フラグをクリア
 *
 * @param companyId - 会社ID
 */
export function clearSubscriptionProcessing(companyId: string): void {
  if (!companyId) {
    return;
  }

  const key = `${PROCESSING_FLAG_PREFIX}${companyId}`;

  try {
    localStorage.removeItem(key);
    logger.info('[subscriptionProcessingLock] Processing flag cleared', { companyId });
  } catch (error) {
    logger.error('[subscriptionProcessingLock] Failed to clear processing flag', error);
  }
}

/**
 * 契約処理中かどうかをチェック
 *
 * @param companyId - 会社ID
 * @returns 処理中の場合true
 */
export function isSubscriptionProcessing(companyId: string): boolean {
  const state = getSubscriptionProcessing(companyId);
  return state?.isProcessing ?? false;
}

/**
 * 経過時間を取得（分単位）
 *
 * @param startedAt - 開始時刻（ISO 8601形式）
 * @returns 経過分数
 */
export function getElapsedMinutes(startedAt: string): number {
  const elapsed = Date.now() - new Date(startedAt).getTime();
  return Math.floor(elapsed / 60000);
}

/**
 * 経過時間を取得（秒単位）
 *
 * @param startedAt - 開始時刻（ISO 8601形式）
 * @returns 経過秒数
 */
export function getElapsedSeconds(startedAt: string): number {
  const elapsed = Date.now() - new Date(startedAt).getTime();
  return Math.floor(elapsed / 1000);
}
